import { ReactElement } from 'react'

import isIE from 'const/isIE'
import i18n from 'strings/i18n'
import styled from 'styled-components'
import CLFooter from 'syf-component-library/ui/patterns/Footer'
import { NavigationContainer } from 'pages/PageLayout/subcomponents'
import { Outlet } from 'react-router-dom'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import mediaQueries from 'syf-component-library/const/mediaQueries'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'

export const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

export const RouteWrapper = styled((props) =>
  // eslint-disable-next-line
  isIE ? <div {...props} role="main" /> : <main {...props} />
)`
  flex-grow: 1;
  @media ${mediaQueries.lessThanMedium} {
    display: flex;
    flex-direction: column;
  }
`

interface OutletWrapperHOCProps {
  children: React.ReactNode
}

export const OutletWrapperHOC: React.FC<OutletWrapperHOCProps> = ({
  children
}) => {
  return (
    <>
      {children}
      <Outlet />
    </>
  )
}
/**
 * Temporary v3 Footer overrides for brand refresh migration.
 *
 * These overrides apply custom styling to the CLFooter component to achieve
 * the v3 design system look and feel. Once the @syf-cl provides
 * native v3 Footer replacements, these styled overrides should be removed and
 * the component should be migrated to use the new v3 component directly.
 *
 * Note: Remove once v3 Footer component is available in @syf-cl
 */
const FooterV3Overrides = styled(CLFooter)`
  background-color: var(--syfds-color-background-default);

  display: flex;
  align-items: center;
  flex-direction: column;

  padding: 24px 96px 12px 96px;

  @media ${mediaQueries.lessThanLarge} {
    padding: 24px 48px 12px 48px;
  }

  & ul {
    display: flex;
    gap: 12px 56px;
    flex-wrap: wrap;

    & li {
      padding-bottom: 0;
      margin-right: 0 !important;
    }
  }

  @media ${mediaQueries.lessThanMedium} {
    padding: 24px 24px 12px 24px;

    & ul {
      flex-direction: column;
      gap: 12px;
    }
  }

  @media ${mediaQueries.lessThanSmall} {
    padding: 24px 16px 12px 16px;
  }

  /** Top Paragraph of Footer */
  & > p {
    color: var(--syfds-color-content-default);
    font-family: var(--syfds-typography-body-small-font-family);
    font-size: var(--syfds-typography-body-small-font-size);
    font-weight: var(--syfds-typography-body-small-font-weight);
    line-height: var(--syfds-typography-body-small-line-height);
    letter-spacing: var(--syfds-typography-body-small-letter-spacing);
    padding-left: 0;
    padding-right: 0;
  }

  /** Divider */
  & > div {
    margin: 12px 0;
    height: 2px;
    width: 100%;
    background-color: var(--syfds-color-background-subtle-default);
  }

  /** Links, Buttons */
  & ul a,
  & ul button {
    padding: 0;
    color: var(--syfds-link-color-content-default);

    & path {
      fill: var(--syfds-link-color-content-default);
    }

    &:hover {
      color: var(--syfds-link-color-content-hover);

      & path {
        fill: var(--syfds-link-color-content-hover);
      }
    }
    &:active {
      color: var(--syfds-link-color-content-active);

      & path {
        fill: var(--syfds-link-color-content-active);
      }
    }
    &:visited {
      color: var(--syfds-link-color-content-visited);
    }
    &:disabled {
      color: var(--syfds-link-color-content-disabled);

      & path {
        fill: var(--syfds-link-color-content-disabled);
      }
    }
  }
`

/**
 * Outer wrapper for the Footer component.
 * Provides consistent layout and background styling for both v3 and legacy footer versions.
 */
const FooterContainer = styled.div`
  display: 'flex';
  justify-content: 'center';
  background: ${({ theme }) => theme.white};
`

/**
 * Footer component with brand refresh v3 migration support.
 *
 * Controls which footer version displays based on the brand refresh feature flag:
 * - When `isBrandRefreshEnabled()` returns true: Renders FooterV3Overrides with v3 design system tokens
 * - When false: Falls back to legacy CLFooter wrapped in NavigationContainer for backward compatibility
 *
 * All footer props are centralized in `footerProps` object for consistency between versions.
 */
export const Footer = (): ReactElement => {
  /**
   * The cookie settings link will only show if both isCookieFooterLinkEnabled and isShowCookieSetting flags are enabled. This is to ensure that we have the infrastructure in place to support the cookie settings page before showing the link in the footer.
   * isShowCookieSetting is also used in initializeOneTrust helper to determine whether to initialize OneTrust and show the cookie settings page in the app. If isShowCookieSetting is disabled, we will not initialize OneTrust which means the cookie settings page will not be accessible. By having both flags, we can enable the footer link and the cookie settings page separately when needed in the future without requiring a code change. For example, if we want to show the footer link but the cookie settings page is not ready yet, we can just enable isCookieFooterLinkEnabled flag and keep isShowCookieSetting flag disabled. Once the cookie settings page is ready, we can then enable isShowCookieSetting flag without needing to touch the code again.
   */
  const isShowCookieSetting =
    isFeatureFlagEnabled('isCookieFooterLinkEnabled') &&
    isFeatureFlagEnabled('isShowCookieSetting')
  const footerLinks = [
    {
      id: '1',
      text: `\u00A9 ${new Date().getFullYear()} Synchrony `,
      url: 'https://www.synchrony.com/',
      dataType: 'footer',
      dataReason: 'Synchrony'
    },
    {
      id: '2',
      text: i18n({ footer: 'privacyPolicy' }),
      url: `https://www.synchrony.com/${i18n({ footer: 'privacyPolicyLink' })}`,
      dataType: 'footer',
      dataReason: 'Online privacy policy'
    },
    {
      id: '3',
      text: i18n({ footer: 'usageAgreement' }),
      url: `https://www.mysynchrony.com/${i18n({
        footer: 'usageAgreementLink'
      })}`,
      dataType: 'footer',
      dataReason: 'Online usage'
    },
    {
      id: '4',
      text: i18n({ footer: 'accessibility' }),
      url: `https://www.synchrony.com/${i18n({
        footer: 'accessibilityLink'
      })}`,
      dataType: 'footer',
      dataReason: 'Accessibility'
    },
    {
      id: '5',
      text: i18n({ footer: 'fraudProtection' }),
      url: `https://www.synchronyfinancial.com/${i18n({
        footer: 'fraudProtectionlink'
      })}`,
      dataType: 'footer',
      dataReason: 'Fraud protection'
    },
    {
      id: '6',
      text: i18n({ footer: 'caDoNotSellShare' }),
      url: `https://www.synchrony.com/${i18n({
        footer: 'caDoNotSellShareLink'
      })}`,
      dataType: 'footer',
      dataReason: 'ca residents – do not sell/share'
    }
  ]

  const footerProps = {
    body: i18n({ footer: 'body' }),
    linkList: footerLinks,
    showCobrowse: true,
    showCookieSetting: isShowCookieSetting,
    cookieSettingButtonLabel: i18n({ footer: 'cookieSettings' }) as string,
    cookieSettingDataReason: 'cookie settings',
    cookieSettingDataType: 'footer',
    'data-test': 'vista-footer',
    'data-cy': 'vista-footer'
  }

  return (
    <FooterContainer>
      {isBrandRefreshEnabled() ? (
        <FooterV3Overrides {...footerProps} />
      ) : (
        <NavigationContainer data-test="vista-footer-container">
          <CLFooter {...footerProps} />
        </NavigationContainer>
      )}
    </FooterContainer>
  )
}
